import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import {
  X, ArrowRight, ArrowLeft, CheckCircle2, Waves,
  User, Mail, Building2, Phone, Briefcase, BarChart2, Globe, Users, Lock, Loader2
} from "lucide-react";
import PasswordStrength, { isPasswordStrong } from "../components/PasswordStrength";

const API = process.env.REACT_APP_BACKEND_URL;

const CARGOS = [
  "CEO / Fundador", "Diretor Comercial", "Gerente de Vendas",
  "Analista de Vendas", "SDR / BDR", "Representante Comercial",
  "Gestor de Marketing", "Socio / Proprietario", "Coordenador de Vendas",
  "Outro",
];

const CRMS = [
  "Nenhum (primeiro CRM)", "HubSpot", "Salesforce", "Pipedrive", "RD CRM",
  "Ploomes", "Moskit", "Agendor", "Zoho CRM", "Monday CRM",
  "Bitrix24", "Microsoft Dynamics", "Freshsales", "Nectar CRM",
  "Planilha (Excel / Google Sheets)", "Outro",
];

const SETORES = [
  "Tecnologia / SaaS", "Automotivo / Veiculos", "Imobiliario",
  "Saude / Medicina", "Educacao", "Varejo / E-commerce",
  "Servicos Financeiros / Fintech", "Agencia / Marketing",
  "Construcao / Engenharia", "Transportes / Logistica",
  "Industria / Manufatura", "Consultoria / Servicos",
  "Telecomunicacoes", "Alimentacao / Franquias", "Outro",
];

const TAMANHOS = [
  "So eu (1 pessoa)", "2 a 5 pessoas", "6 a 10 pessoas",
  "11 a 50 pessoas", "Mais de 50 pessoas",
];

function SelectField({ icon: Icon, label, value, onChange, options, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <select value={value} onChange={e => onChange(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent appearance-none">
          <option value="">Selecione...</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </div>
  );
}

function InputField({ icon: Icon, label, value, onChange, type = "text", placeholder, required, hint, mask }) {
  const handleChange = (e) => {
    let v = e.target.value;
    if (mask === "phone") {
      v = v.replace(/\D/g, "").slice(0, 11);
      if (v.length > 6) v = `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
      else if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`;
      else if (v.length > 0) v = `(${v}`;
    }
    onChange(v);
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input type={type} value={value} onChange={handleChange}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent" />
      </div>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function TrialModal({ onClose }) {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [step, setStep] = useState(1); // 1 = dados pessoais, 2 = perfil, 3 = sucesso
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone]     = useState("");
  const [password, setPassword] = useState("");

  // Step 2
  const [cargo, setCargo]   = useState("");
  const [crm, setCrm]       = useState("");
  const [setor, setSetor]   = useState("");
  const [tamanho, setTamanho] = useState("");

  const step1Valid = name.trim() && email.trim() && company.trim() && isPasswordStrong(password);
  const step2Valid = cargo && setor && tamanho;

  const handleNext = () => {
    setError("");
    if (!name.trim() || !email.trim() || !company.trim()) {
      setError("Preencha nome, e-mail e nome da empresa.");
      return;
    }
    if (password.length < 8 || !isPasswordStrong(password)) {
      setError("A senha nao atende aos requisitos de seguranca.");
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!step2Valid) { setError("Preencha cargo, setor e tamanho da empresa."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/trial/register`, {
        name, email, company, phone, password,
        cargo, crm_atual: crm, setor, tamanho_empresa: tamanho,
        source: document.referrer || "",
      });

      // Save session token for header-based auth
      if (res.data.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }

      // Auto-login: fetch user data with permissions via /auth/me
      try {
        await checkAuth();
        navigate("/dashboard", { replace: true });
      } catch {
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao cadastrar. Tente novamente.");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={step === 3 ? onClose : undefined} />

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden my-8 mx-4 flex-shrink-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#3B82F6] to-blue-600 px-10 pt-9 pb-7">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <img src="/brand/icon-2.png" alt="Pipe4.me" style={{ height: "3rem" }} className="object-contain" />
            </div>
            {step !== 3 && (
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {step === 3 ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Conta criada com sucesso!</h2>
              <p className="text-blue-100 text-sm mt-1">Seu periodo de teste de 14 dias comecou</p>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-white">
                {step === 1 ? "Crie sua conta gratis" : "Conte-nos sobre voce"}
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                {step === 1 ? "14 dias gratis - Sem cartao de credito - Setup em 5 minutos" : "Ajuda-nos a personalizar sua experiencia"}
              </p>

              {/* Step indicator */}
              <div className="flex items-center gap-2 mt-4">
                {[1, 2].map(s => (
                  <div key={s} className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${s <= step ? "bg-white text-[#3B82F6]" : "bg-white/20 text-white"}`}>
                      {s < step ? "\u2713" : s}
                    </div>
                    {s < 2 && <div className={`w-12 h-0.5 ${s < step ? "bg-white" : "bg-white/30"}`} />}
                  </div>
                ))}
                <span className="text-white/70 text-xs ml-1">Etapa {step} de 2</span>
              </div>
            </>
          )}
        </div>

        {/* Body */}
        <div className="px-10 py-7">

          {/* Step 1: Dados pessoais */}
          {step === 1 && (
            <div className="space-y-4">
              <InputField icon={User}      label="Nome completo"    value={name}    onChange={setName}    placeholder="Seu nome"       required />
              <InputField icon={Mail}      label="E-mail"           value={email}   onChange={setEmail}   type="email"
                placeholder="seu@email.com" required />
              <InputField icon={Building2} label="Nome da empresa"  value={company} onChange={setCompany} placeholder="Sua empresa"     required />
              <InputField icon={Phone}     label="Telefone"         value={phone}   onChange={setPhone}   placeholder="(11) 98765-4321" mask="phone" />
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Senha<span className="text-rose-500 ml-0.5">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    data-testid="trial-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Ex: Senh@F0rte!"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
                  />
                </div>
                <PasswordStrength password={password} />
              </div>

              {error && <p className="text-xs text-rose-500 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

              <button
                data-testid="trial-next-btn"
                onClick={handleNext}
                disabled={!step1Valid}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#3B82F6] text-white rounded-xl font-semibold text-sm hover:bg-blue-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                Continuar <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 2: Perfil */}
          {step === 2 && (
            <div className="space-y-4">
              <SelectField icon={Briefcase} label="Seu cargo"              value={cargo}   onChange={setCargo}   options={CARGOS}   required />
              <SelectField icon={BarChart2} label="CRM que usa atualmente" value={crm}     onChange={setCrm}     options={CRMS}             />
              <SelectField icon={Globe}     label="Setor da empresa"       value={setor}   onChange={setSetor}   options={SETORES}  required />
              <SelectField icon={Users}     label="Tamanho da equipe"      value={tamanho} onChange={setTamanho} options={TAMANHOS} required />

              {error && <p className="text-xs text-rose-500 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

              <div className="flex gap-3 pt-1">
                <button onClick={() => { setStep(1); setError(""); }}
                  className="flex items-center gap-1.5 px-4 py-3 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Voltar
                </button>
                <button
                  data-testid="trial-submit-btn"
                  onClick={handleSubmit}
                  disabled={loading || !step2Valid}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#3B82F6] text-white rounded-xl font-semibold text-sm hover:bg-blue-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                  {loading ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Criando conta...</span>
                  ) : (
                    <>Criar conta gratis <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Sucesso (fallback) */}
          {step === 3 && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-slate-600">Sua conta foi criada. Faca login para comecar.</p>
              <div className="space-y-2 pt-1">
                {["14 dias gratis, sem cartao", "Pipeline configurado", "Acesso a todos os recursos"].map((item, i) => (
                  <div key={i} className="flex items-center justify-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{item}
                  </div>
                ))}
              </div>
              <button
                data-testid="trial-go-login-btn"
                onClick={() => { onClose(); navigate("/login"); }}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#3B82F6] text-white rounded-xl font-semibold text-sm hover:bg-blue-600 transition-all">
                Ir para o login <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 3 && (
          <div className="px-10 pb-6 text-center">
            <p className="text-[11px] text-slate-400">
              Ao criar sua conta voce concorda com os{" "}
              <a href="/termos-de-uso" target="_blank" className="text-[#3B82F6] hover:underline">Termos de Uso</a>
              {" "}e a{" "}
              <a href="/politica-de-privacidade" target="_blank" className="text-[#3B82F6] hover:underline">Politica de Privacidade</a>
              {" "}do Pipe4.me.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
